
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Languages } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface ModuleContentEditorProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: string;
  initialContent: string;
  moduleTitle: string;
  courseId: string;
}

export function ModuleContentEditor({
  isOpen,
  onClose,
  moduleId,
  initialContent,
  moduleTitle,
  courseId,
}: ModuleContentEditorProps) {
  const [content, setContent] = React.useState(initialContent);
  const queryClient = useQueryClient();

  // Check if there's Spanish translation available
  const [hasSpanishTranslation, setHasSpanishTranslation] = React.useState(false);
  const [spanishContent, setSpanishContent] = React.useState("");
  const [activeTab, setActiveTab] = React.useState<"english" | "spanish">("english");
  const [isCheckingTranslation, setIsCheckingTranslation] = React.useState(false);

  React.useEffect(() => {
    const checkForSpanishTranslation = async () => {
      setIsCheckingTranslation(true);
      try {
        // Parse moduleId to get module and part numbers
        const moduleIdParts = moduleId.split('-');
        const moduleNumber = parseInt(moduleIdParts[0], 10);
        const partNumber = parseInt(moduleIdParts[1], 10);

        // Query for Spanish version of this same module
        const { data, error } = await supabase
          .from('course_modules')
          .select('content')
          .eq('module_number', moduleNumber)
          .eq('part_number', partNumber)
          .eq('language', 'Spanish')
          .eq('course_id', courseId)
          .single();

        if (error) {
          console.error('Error checking Spanish translation:', error);
          setHasSpanishTranslation(false);
        } else if (data) {
          setHasSpanishTranslation(true);
          setSpanishContent(data.content || "");
        } else {
          setHasSpanishTranslation(false);
        }
      } catch (error) {
        console.error('Error checking Spanish translation:', error);
        setHasSpanishTranslation(false);
      } finally {
        setIsCheckingTranslation(false);
      }
    };

    if (isOpen && moduleId) {
      checkForSpanishTranslation();
    }
  }, [isOpen, moduleId, courseId]);

  const handleSave = async () => {
    try {
      const { error } = await supabase
        .from('course_modules')
        .update({ 
          content, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', moduleId);

      if (error) throw error;

      toast.success('Module content updated successfully');
      queryClient.invalidateQueries({ queryKey: ['courseModules', courseId] });
      onClose();
    } catch (error) {
      console.error('Error updating module content:', error);
      toast.error('Failed to update module content');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Edit Content: {moduleTitle}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 h-full">
          {hasSpanishTranslation ? (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "english" | "spanish")} className="w-full">
              <TabsList className="grid grid-cols-2 w-full mb-2">
                <TabsTrigger value="english">English</TabsTrigger>
                <TabsTrigger value="spanish">Spanish</TabsTrigger>
              </TabsList>
              <TabsContent value="english" className="flex-1 h-[500px]">
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="flex-1 min-h-[500px] font-mono h-full"
                />
              </TabsContent>
              <TabsContent value="spanish" className="flex-1 h-[500px]">
                <Textarea
                  value={spanishContent}
                  readOnly
                  className="flex-1 min-h-[500px] font-mono h-full bg-muted/40"
                />
              </TabsContent>
            </Tabs>
          ) : (
            <>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="flex-1 min-h-[500px] font-mono"
              />
              {isCheckingTranslation ? (
                <div className="text-center text-xs text-muted-foreground">
                  Checking for Spanish translation...
                </div>
              ) : (
                <div className="text-center text-xs text-muted-foreground">
                  No Spanish translation available. Use the course-level translation to generate one.
                </div>
              )}
            </>
          )}
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
